import { Component, input, signal, computed, OnInit, OnDestroy, inject, DestroyRef, effect } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

@Component({
	selector: 'sp-progress-dots',
	standalone: true,
	templateUrl: './progress-dots.component.html',
	styleUrl: './progress-dots.component.scss'
})
export class ProgressDotsComponent implements OnInit, OnDestroy {
	// Inputs
	routes = input<string[]>();
	clickable = input<boolean>(false);
	currentStep = input<number | null>(null);
	totalSteps = input<number | null>(null); // Number of steps when routes are not provided

	// Internal state
	currentIndex = signal<number>(0);

	// Computed: number of dots to display
	totalDots = computed(() => {
		const routes = this.routes();
		if (routes && routes.length > 0) {
			return routes.length;
		}
		const steps = this.totalSteps();
		if (steps !== null && steps > 0) {
			return steps;
		}
		// Default to 1 if neither is provided
		return 1;
	});

	// Computed: array of indices for template iteration
	dotsArray = computed(() => {
		return Array.from({ length: this.totalDots() }, (_, i) => i);
	});

	// Services
	private router = inject(Router);
	private destroyRef = inject(DestroyRef);

	constructor() {
		// React to changes in routes and currentStep inputs
		effect(() => {
			this.updateIndex();
		});
	}

	ngOnInit(): void {
		// Initial check
		this.updateIndex();

		// Listen for navigation changes (only relevant if using routes)
		// Only subscribe if routes are provided, otherwise currentStep will handle updates
		const routes = this.routes();
		if (routes && routes.length > 0) {
			this.router.events
				.pipe(
					filter(event => event instanceof NavigationEnd),
					takeUntilDestroyed(this.destroyRef)
				)
				.subscribe(() => {
					// Only update if routes are still provided
					const currentRoutes = this.routes();
					if (currentRoutes && currentRoutes.length > 0) {
						this.updateIndex();
					}
				});
		}
	}

	ngOnDestroy(): void {
		// Cleanup handled by takeUntilDestroyed and DestroyRef
		// No additional cleanup needed
		// This method is required by OnDestroy interface
		void 0;
	}

	/**
	 * Update the current index based on priority: routes first, then currentStep
	 */
	private updateIndex(): void {
		const routes = this.routes();
		const step = this.currentStep();

		// Priority 1: If routes are provided, use route-based detection
		if (routes && routes.length > 0) {
			this.updateCurrentIndex();
		}
		// Priority 2: If no routes but currentStep is provided (including 0), use currentStep
		else if (step !== null && step !== undefined) {
			this.currentIndex.set(step);
		}
		// Priority 3: Default to 0 if neither is available
		else {
			this.currentIndex.set(0);
		}
	}

	/**
	 * Update the current index based on the current router URL
	 * Only used when routes are provided
	 */
	private updateCurrentIndex(): void {
		const routes = this.routes();
		if (!routes || routes.length === 0) {
			return;
		}

		const currentUrl = this.router.url;
		const index = routes.findIndex(route => currentUrl.includes(route));
		this.currentIndex.set(index >= 0 ? index : 0);
	}

	/**
	 * Handle dot click navigation
	 */
	onDotClick(index: number): void {
		if (this.clickable()) {
			const routes = this.routes();
			if (routes && routes.length > 0 && routes[index]) {
				void this.router.navigate([routes[index]]);
			}
		}
	}
}
